import { useEffect, useSyncExternalStore } from 'react'

/** A list kept on the server and shared by the pages that read it (manual
 *  sensors, calculated sensors, Alarm Lab strategies). It replaced
 *  workspace v5's browser storage: one fetch, held here, so the
 *  synchronous readers the fork wrote (`readManualSensors()` in the Data
 *  Explorer's source list) keep working, and every page sees a write as
 *  soon as one page has made it.
 *
 *  - `snapshot()` is what is held now; `items` is empty until the first
 *    answer, and `loaded` says whether one has come.
 *  - `ready()` waits for the first answer (an analysis opened cold must not
 *    read "no longer available" before the list has arrived), and fails if
 *    the list has never loaded: an empty list it could not fetch is not an
 *    empty list. A later call asks again.
 *  - `refresh()` asks again, always afresh: a write calls it, and must see
 *    itself, so it never joins a load that began before it. Only the newest
 *    answer is kept, and `event` on the window asks too, so code outside
 *    React can announce a change.
 *  - A failed load keeps what was held and says why in `error`.
 *  - `reset()` (signing out) forgets everything, and an answer still on its
 *    way for the person before is dropped when it comes. */
export interface RemoteState<T> { items: T[]; loaded: boolean; error: string }
export interface RemoteCollection<T> {
  snapshot(): RemoteState<T>
  ready(): Promise<T[]>
  refresh(): Promise<T[]>
  /** Replace what is held (a write that returned the new list), and tell
   *  every reader. */
  set(items: T[]): void
  subscribe(listener: () => void): () => void
  /** Forget everything (signing out); the next reader loads again. */
  reset(): void
}

export function remoteCollection<T>(load: () => Promise<T[]>, event?: string): RemoteCollection<T> {
  let state: RemoteState<T> = { items: [], loaded: false, error: '' }
  let inflight: Promise<T[]> | null = null
  // Which load is newest, and whose (reset starts a new era).
  let latest = 0, era = 0
  const listeners = new Set<() => void>()
  const publish = (next: RemoteState<T>) => {
    state = next
    listeners.forEach(listener => listener())
  }
  // Listened for from first use, never on import: importing a module must
  // not touch the window (the demo's isolation test holds that line).
  let listening = false
  const listen = () => {
    if (listening || !event || typeof window === 'undefined' || typeof window.addEventListener !== 'function') return
    listening = true
    window.addEventListener(event, () => { void refresh() })
  }
  const refresh = (): Promise<T[]> => {
    listen()
    const mine = ++latest, ours = era
    const current = () => mine === latest && ours === era
    const request: Promise<T[]> = load().then(items => {
      if (current()) publish({ items, loaded: true, error: '' })
      return items
    }, (failure: unknown) => {
      if (current()) publish({ ...state, error: failure instanceof Error ? failure.message : 'Could not load.' })
      return state.items
    }).finally(() => { if (inflight === request) inflight = null })
    inflight = request
    return request
  }
  const loaded = (items: T[]) => state.loaded ? items : Promise.reject(new Error(state.error || 'Could not load.'))
  return {
    snapshot: () => state,
    ready: () => state.loaded ? Promise.resolve(state.items) : (inflight ?? refresh()).then(loaded),
    refresh,
    set: items => publish({ items, loaded: true, error: '' }),
    subscribe(listener) { listen(); listeners.add(listener); return () => { listeners.delete(listener) } },
    reset() { era++; state = { items: [], loaded: false, error: '' }; inflight = null },
  }
}

/** The collection as React state: loads on first use, and draws again on
 *  every change (a failed load shows in `error`). */
export function useRemote<T>(collection: RemoteCollection<T>): RemoteState<T> {
  const state = useSyncExternalStore(collection.subscribe, collection.snapshot, collection.snapshot)
  useEffect(() => { collection.ready().catch(() => { /* in state.error */ }) }, [collection])
  return state
}
